import axios from "axios";

import {
  KillHistoryResultType,
  MOCK_KILL_HISTORY_ENABLED,
  MOCK_KILL_HISTORY_ENTRIES,
  getKillHistoryResultLabel,
} from "../constants/killHistory";
import { SERVER_URL } from "../config/config";
import { ProfileNames } from "../types/ProfileName";
import { KillHistoryEntry } from "../types/KillHistoryEntry";

const readOptionalNumber = (
  source: Record<string, unknown>,
  keys: string[]
): number | undefined => {
  for (const key of keys) {
    const raw = source[key];
    if (raw !== undefined && raw !== null && raw !== "") {
      const numeric = Number(raw);
      if (!Number.isNaN(numeric)) {
        return numeric;
      }
    }
  }
  return undefined;
};

const readOptionalString = (
  source: Record<string, unknown>,
  keys: string[]
): string | undefined => {
  for (const key of keys) {
    const raw = source[key];
    if (typeof raw === "string" && raw.trim()) {
      return raw.trim();
    }
  }
  return undefined;
};

const parseResultType = (value: unknown): KillHistoryResultType => {
  const numeric = Number(value);

  if (numeric === KillHistoryResultType.DoubleKill) {
    return KillHistoryResultType.DoubleKill;
  }

  if (numeric === KillHistoryResultType.BackfireKill) {
    return KillHistoryResultType.BackfireKill;
  }

  return KillHistoryResultType.SingleKill;
};

const resolveProfileName = (
  address: string,
  profileNames: ProfileNames[]
): string | undefined => {
  const normalized = address.toLowerCase();
  return profileNames.find(
    (profile) => profile.address?.toLowerCase() === normalized
  )?.name;
};

const normalizeApiEntry = (
  raw: Record<string, unknown>,
  index: number
): KillHistoryEntry | null => {
  const attackerAddress = String(
    raw.attackerAddress ?? raw.attacker ?? raw.attacker_address ?? ""
  );
  const victimAddress = String(
    raw.victimAddress ?? raw.victim ?? raw.victim_address ?? ""
  );

  if (!attackerAddress || !victimAddress) {
    return null;
  }

  const resultRaw =
    raw.resultType ?? raw.result_type ?? raw.result ?? raw.killType;
  let resultType = KillHistoryResultType.SingleKill;

  if (typeof resultRaw === "string") {
    const normalized = resultRaw.toLowerCase();
    if (normalized.includes("double")) {
      resultType = KillHistoryResultType.DoubleKill;
    } else if (normalized.includes("backfire")) {
      resultType = KillHistoryResultType.BackfireKill;
    }
  } else {
    resultType = parseResultType(resultRaw);
  }

  const timestamp = Number(
    raw.timestamp ?? raw.blockTimestamp ?? raw.created_at ?? 0
  );

  return {
    id: String(raw.id ?? `api-kill-${index}-${attackerAddress}-${victimAddress}`),
    timestamp,
    attackerAddress,
    victimAddress,
    attackerName: raw.attackerName
      ? String(raw.attackerName)
      : raw.attacker_name
        ? String(raw.attacker_name)
        : undefined,
    victimName: raw.victimName
      ? String(raw.victimName)
      : raw.victim_name
        ? String(raw.victim_name)
        : undefined,
    attackerIsDead: Boolean(
      raw.attackerIsDead ?? raw.attacker_is_dead ?? raw.attackerDead
    ),
    victimIsDead: Boolean(
      raw.victimIsDead ?? raw.victim_is_dead ?? raw.victimDead
    ),
    resultType,
    bullets: raw.bullets !== undefined ? Number(raw.bullets) : undefined,
    attackerBullets: readOptionalNumber(raw, [
      "attackerBullets",
      "attacker_bullets",
    ]),
    backfireBullets: readOptionalNumber(raw, [
      "backfireBullets",
      "backfire_bullets",
    ]),
    killerWords: readOptionalString(raw, [
      "killerWords",
      "killer_words",
      "lastWords",
      "last_words",
      "message",
    ]),
    transactionHash: raw.transactionHash
      ? String(raw.transactionHash)
      : raw.transaction_hash
        ? String(raw.transaction_hash)
        : undefined,
    blockNumber:
      raw.blockNumber !== undefined
        ? Number(raw.blockNumber)
        : raw.block_number !== undefined
          ? Number(raw.block_number)
          : undefined,
  };
};

export const fetchKillHistoryFromApi = async (): Promise<KillHistoryEntry[]> => {
  try {
    const response = await axios.get(`${SERVER_URL}/kill/history`);
    const payload = response.data as
      | Record<string, unknown>[]
      | { history?: Record<string, unknown>[]; data?: Record<string, unknown>[] }
      | unknown;

    const rows: Record<string, unknown>[] = Array.isArray(payload)
      ? payload
      : Array.isArray(
            (payload as { history?: Record<string, unknown>[] })?.history
          )
        ? (payload as { history: Record<string, unknown>[] }).history
        : Array.isArray(
              (payload as { data?: Record<string, unknown>[] })?.data
            )
          ? (payload as { data: Record<string, unknown>[] }).data
          : [];

    return rows
      .map((row, index) => normalizeApiEntry(row, index))
      .filter(
        (entry: KillHistoryEntry | null): entry is KillHistoryEntry =>
          entry !== null
      );
  } catch {
    return [];
  }
};

const getMockKillHistoryEntries = (
  profileNames: ProfileNames[]
): KillHistoryEntry[] =>
  [...MOCK_KILL_HISTORY_ENTRIES]
    .map((entry) => ({
      ...entry,
      attackerName:
        entry.attackerName ??
        resolveProfileName(entry.attackerAddress, profileNames),
      victimName:
        entry.victimName ??
        resolveProfileName(entry.victimAddress, profileNames),
    }))
    .sort((a, b) => b.timestamp - a.timestamp);

const enrichKillHistoryEntries = (
  entries: KillHistoryEntry[],
  profileNames: ProfileNames[]
): KillHistoryEntry[] =>
  entries
    .map((entry) => ({
      ...entry,
      attackerName:
        entry.attackerName ??
        resolveProfileName(entry.attackerAddress, profileNames),
      victimName:
        entry.victimName ??
        resolveProfileName(entry.victimAddress, profileNames),
    }))
    .sort((a, b) => b.timestamp - a.timestamp);

export const fetchKillHistoryEntries = async (
  profileNames: ProfileNames[]
): Promise<KillHistoryEntry[]> => {
  if (MOCK_KILL_HISTORY_ENABLED) {
    return getMockKillHistoryEntries(profileNames);
  }

  return enrichKillHistoryEntries(
    await fetchKillHistoryFromApi(),
    profileNames
  );
};

export const formatKillHistoryPlayerLabel = (
  address: string,
  name?: string
): string => (name ? name : address);

export { getKillHistoryResultLabel };
